<template>
  <!--
    The tour's popover (spec 25 section 3.1): ours, not the spotlight
    library's, so it is drawn with this design system's tokens and sits under
    the token-purity gate like every other lw component.

    role="dialog" with aria-modal="false": it describes the page beside it and
    never takes the page away. The page stays live and clickable behind it.
    The host focuses it when a step opens and announces step changes through
    its own live region; this component only renders what it is given.
  -->
  <section
    ref="card"
    class="lw-c-tour-card"
    :class="{
      'lw-c-tour-card--docked': docked,
      'lw-c-tour-card--center': centered,
      'lw-c-tour-card--corner': inCorner,
      'lw-c-tour-card--corner-left': inCorner && cornerSide.endsWith('left'),
      'lw-c-tour-card--corner-top': inCorner && cornerSide.startsWith('top'),
    }"
    role="dialog"
    aria-modal="false"
    :aria-labelledby="titleId"
    :aria-describedby="bodyId"
    tabindex="-1"
    :style="docked || centered ? null : corner ? cornerStyle : positionStyle"
  >
    <p class="lw-c-tour-card__chapter">
      <span>{{ chapterTitle }}</span>
      <span class="lw-c-tour-card__count" data-test="tour-count">{{
        countText || `${stepNumber} of ${total}`
      }}</span>
    </p>
    <h2 :id="titleId" class="lw-c-tour-card__title">{{ title }}</h2>
    <!--
      Spec 53 4.7: a course chapter says where it sits in its course, under
      the title. The segments are for the eye; the sentence says the same.
    -->
    <div v-if="course" class="lw-c-tour-card__course" data-test="tour-course">
      <p class="lw-c-tour-card__course-line">
        {{ course.title }} · {{ course.chapter }} of {{ course.chapters }} · {{ course.name }}
      </p>
      <ol class="lw-c-tour-card__course-bar" aria-hidden="true">
        <li
          v-for="n in course.chapters"
          :key="n"
          class="lw-c-tour-card__course-seg"
          :class="{
            'lw-c-tour-card__course-seg--done': n < course.chapter,
            'lw-c-tour-card__course-seg--now': n === course.chapter,
          }"
        />
      </ol>
    </div>
    <p :id="bodyId" class="lw-c-tour-card__body">{{ body }}</p>

    <!--
      What to do now (Mark, 2026-10-12: the walkthroughs are training). The
      acknowledgement of what the visitor just did, when they did it ("Great.
      Now the equipment and the reference."), then the one thing to do next,
      in words; the ring and its label on the page say where.
    -->
    <div v-if="ack || instruction" class="lw-c-tour-card__now" data-test="tour-now">
      <p v-if="ack" class="lw-c-tour-card__ack" data-test="tour-ack">{{ ack }}</p>
      <p v-if="instruction" class="lw-c-tour-card__instruction" data-test="tour-instruction">
        {{ instruction }}
      </p>
    </div>

    <!--
      Spec 53 9.1: the reason for the step, one sentence, behind a small
      "Why?" so the card stays short. Closed again on every new step.
    -->
    <div v-if="why" class="lw-c-tour-card__why">
      <LwButton
        variant="link"
        size="sm"
        :aria-expanded="String(whyOpen)"
        :aria-controls="whyId"
        data-test="tour-why"
        @click="whyOpen = !whyOpen"
      >
        Why?
      </LwButton>
      <p :id="whyId" class="lw-c-tour-card__why-text" :hidden="!whyOpen" data-test="tour-why-text">
        {{ why }}
      </p>
    </div>

    <!--
      Spec 33: what the walkthrough itself is doing or waiting on ("Saving...",
      a refusal in plain words). Visible here; the host announces the same
      line through its own polite live region.
    -->
    <p v-if="status" class="lw-c-tour-card__status" data-test="tour-status">{{ status }}</p>

    <!--
      Spec 33: the step's own controls (Try again, Stop, a branch point's
      choices, and, only once a step has stalled, a quiet "Do this step for
      me" link). All secondary: the page under the card has its own primary,
      and the card is a guest.
    -->
    <div v-if="controls.length" class="lw-c-tour-card__controls">
      <LwButton
        v-for="control in controls"
        :key="control.id"
        :variant="control.variant || 'secondary'"
        size="sm"
        :data-test="`tour-control-${control.id}`"
        @click="$emit('control', control.id)"
      >
        {{ control.label }}
      </LwButton>
    </div>

    <p v-if="footnote" class="lw-c-tour-card__footnote" data-test="tour-price">{{ footnote }}</p>

    <div v-if="actions.length" class="lw-c-tour-card__cta">
      <LwButton
        v-for="action in actions"
        :key="action.label"
        :variant="action.primary ? 'primary' : 'secondary'"
        :href="action.href"
        target="_blank"
        rel="noopener"
        @click="$emit('action', action)"
      >
        {{ action.label }}
      </LwButton>
    </div>

    <div class="lw-c-tour-card__nav">
      <!-- A walkthrough's row also holds Skip and Back, so its exit says
           "Leave" and names what it leaves to a screen reader. -->
      <LwButton
        variant="link"
        size="sm"
        data-test="tour-exit"
        :aria-label="isLast ? null : `Leave the ${noun}`"
        @click="$emit('exit')"
      >
        {{ isLast ? closeLabel : noun === 'walkthrough' ? 'Leave' : `Leave the ${noun}` }}
      </LwButton>
      <span class="lw-c-tour-card__spacer" />
      <LwButton
        v-if="skippable"
        variant="link"
        size="sm"
        data-test="tour-skip"
        @click="$emit('skip')"
      >
        Skip this step
      </LwButton>
      <LwButton
        v-if="!isFirst && canBack"
        variant="secondary"
        size="sm"
        data-test="tour-back"
        @click="$emit('back')"
      >
        Back
      </LwButton>
      <!--
        Secondary, not primary: LwButton holds the app to one primary per view,
        and the page under this card usually has its own. The card is a guest.
        The closing card's first action is the one primary the tour ever mounts.
      -->
      <LwButton
        v-if="!isLast && showNext"
        variant="secondary"
        size="sm"
        data-test="tour-next"
        @click="$emit('next')"
      >
        Next
      </LwButton>
    </div>
  </section>
</template>

<script>
import LwButton from './LwButton.vue';

let uid = 0;

export default {
  name: 'LwTourCard',
  components: { LwButton },
  props: {
    chapterTitle: { type: String, default: '' },
    title: { type: String, required: true },
    body: { type: String, default: '' },
    stepNumber: { type: Number, required: true },
    total: { type: Number, required: true },
    isFirst: { type: Boolean, default: false },
    isLast: { type: Boolean, default: false },
    // [{ label, href, primary }] on the closing card.
    actions: { type: Array, default: () => [] },
    // One more line above the actions: the list price, on the closing card.
    footnote: { type: String, default: '' },
    // Under 720px the card docks to the bottom of the viewport, full width.
    docked: { type: Boolean, default: false },
    // No target on screen (or a step that has none): centred over the scrim.
    centered: { type: Boolean, default: false },
    // A walkthrough's card on a desk: docked in the bottom right corner, so
    // it stays where the visitor is reading while the ring moves.
    corner: { type: Boolean, default: false },
    // Which corner (geometry.js CORNERS): the host moves the card out of the
    // way of the control it rings, an open menu or a dialog's buttons.
    cornerSide: { type: String, default: 'bottom-right' },
    // A top corner sits this far down, below the app's header.
    cornerTop: { type: Number, default: 0 },
    // What the visitor just did, acknowledged ("Great."), and what to do now.
    ack: { type: String, default: '' },
    instruction: { type: String, default: '' },
    // { top, left } in viewport pixels, worked out by the host from the target.
    position: { type: Object, default: () => ({ top: 0, left: 0 }) },
    // Spec 33, all optional so the story's cards are unchanged:
    // what an action step is doing now,
    status: { type: String, default: '' },
    // its controls, [{ id, label }], emitted back as `control`,
    controls: { type: Array, default: () => [] },
    // whether Next is offered (an action step advances on its outcome),
    showNext: { type: Boolean, default: true },
    // whether Back is offered (never across a step that wrote to the server),
    canBack: { type: Boolean, default: true },
    // and whether the step can be skipped.
    skippable: { type: Boolean, default: false },
    // What the visitor is in: 'tour' (the story) or 'walkthrough' (a task).
    noun: { type: String, default: 'tour' },
    // Spec 53, a training module: the count in words ("Step 2 of 4"),
    countText: { type: String, default: '' },
    // its course, { title, chapter, chapters, name }, for a course chapter,
    course: { type: Object, default: null },
    // the step's reason, behind "Why?",
    why: { type: String, default: '' },
    // and what the last card's way out says ("Finish").
    closeLabel: { type: String, default: 'Close' },
  },
  emits: ['next', 'back', 'exit', 'action', 'control', 'skip'],
  data() {
    uid += 1;
    return {
      titleId: `lw-tour-title-${uid}`,
      bodyId: `lw-tour-body-${uid}`,
      whyId: `lw-tour-why-${uid}`,
      whyOpen: false,
    };
  },
  computed: {
    positionStyle() {
      return { top: `${this.position.top}px`, left: `${this.position.left}px` };
    },
    inCorner() {
      return this.corner && !this.docked && !this.centered;
    },
    cornerStyle() {
      return this.cornerSide.startsWith('top')
        ? { top: `calc(${this.cornerTop}px + var(--lw-space-5))` }
        : null;
    },
  },
  watch: {
    // A new step (or a new reason) starts with its reason folded away.
    why() {
      this.whyOpen = false;
    },
    title() {
      this.whyOpen = false;
    },
    instruction() {
      this.whyOpen = false;
    },
  },
  methods: {
    focus() {
      if (this.$refs.card) this.$refs.card.focus({ preventScroll: true });
    },
    size() {
      const el = this.$refs.card;
      return el ? { width: el.offsetWidth, height: el.offsetHeight } : { width: 0, height: 0 };
    },
  },
};
</script>

<style scoped>
/*
  Above the spotlight's scrim, which the library draws at z-index 10000. Built
  from the toast token rather than written as a number, so the one place that
  knows the app's stacking order still decides the order here.
*/
.lw-c-tour-card {
  background: var(--lw-elev-3-surface);
  border: 1px solid var(--lw-elev-3-line);
  border-radius: var(--lw-radius-md);
  box-shadow: var(--lw-elev-3-shadow);
  color: var(--lw-fg-primary);
  max-width: calc(100vw - var(--lw-space-6));
  outline: none;
  padding: var(--lw-space-5);
  position: fixed;
  width: 22.5rem;
  z-index: calc(var(--lw-z-toast) * 11);
}

/*
  The spotlight library turns mouse events OFF for the whole page while its
  scrim is up (`.driver-active * { pointer-events: none }`) and back on only
  for the highlighted element and ITS OWN popover. This card is ours, so
  without this rule every click on Next fell straight through it to whatever
  was underneath, and the tour could only be driven with the arrow keys
  (found by Mark on the live demo, 2026-09-21). `:deep(*)` because the
  library's rule hits every descendant, the buttons included.
*/
.lw-c-tour-card,
.lw-c-tour-card :deep(*) {
  pointer-events: auto;
}

.lw-c-tour-card--center {
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 26rem;
}

.lw-c-tour-card--corner {
  bottom: var(--lw-space-5);
  left: auto;
  right: var(--lw-space-5);
  top: auto;
}

/* The other corners; a top one's distance from the top is set inline (cornerStyle). */
.lw-c-tour-card--corner-left {
  left: var(--lw-space-5);
  right: auto;
}

.lw-c-tour-card--corner-top {
  bottom: auto;
}

.lw-c-tour-card--docked {
  border-radius: var(--lw-radius-md) var(--lw-radius-md) 0 0;
  bottom: 0;
  left: 0;
  max-width: none;
  right: 0;
  top: auto;
  transform: none;
  width: auto;
}

.lw-c-tour-card__chapter {
  color: var(--lw-fg-secondary);
  display: flex;
  font: var(--lw-type-label);
  justify-content: space-between;
  letter-spacing: var(--lw-type-label-tracking);
  margin: 0 0 var(--lw-space-2);
  text-transform: var(--lw-type-label-transform);
}

.lw-c-tour-card__title {
  font: var(--lw-type-subhead);
  margin: 0 0 var(--lw-space-2);
}

.lw-c-tour-card__body {
  font: var(--lw-type-body-sm);
  margin: 0 0 var(--lw-space-4);
}

/*
  The instruction is the card's working line, so it is set apart: an amber
  rule down its left edge, the same amber as the ring it refers to.
*/
.lw-c-tour-card__now {
  border-left: var(--lw-focus-ring-width) solid var(--lw-focus-ring-color);
  margin: 0 0 var(--lw-space-4);
  padding-left: var(--lw-space-3);
}

.lw-c-tour-card__ack {
  color: var(--lw-fg-secondary);
  font: var(--lw-type-body-sm);
  margin: 0 0 var(--lw-space-1);
}

.lw-c-tour-card__instruction {
  font: var(--lw-type-body-sm);
  font-weight: 700;
  margin: 0;
}

/* Spec 53 4.7: where a course chapter sits in its course. */
.lw-c-tour-card__course {
  margin: 0 0 var(--lw-space-3);
}

.lw-c-tour-card__course-line {
  color: var(--lw-fg-secondary);
  font: var(--lw-type-caption);
  margin: 0 0 var(--lw-space-1);
}

.lw-c-tour-card__course-bar {
  display: flex;
  gap: var(--lw-space-1);
  list-style: none;
  margin: 0;
  padding: 0;
}

.lw-c-tour-card__course-seg {
  background: var(--lw-elev-3-line);
  border-radius: var(--lw-radius-pill);
  flex: 1;
  height: var(--lw-space-1);
}

.lw-c-tour-card__course-seg--done {
  background: var(--lw-success-fg);
}

.lw-c-tour-card__course-seg--now {
  background: var(--lw-focus-ring-color);
}

/* Spec 53 9.1: "Why?" is a quiet link, its sentence under it. */
.lw-c-tour-card__why {
  margin: 0 0 var(--lw-space-3);
}

.lw-c-tour-card__why-text {
  color: var(--lw-fg-secondary);
  font: var(--lw-type-body-sm);
  margin: var(--lw-space-1) 0 0;
}

.lw-c-tour-card__status {
  color: var(--lw-fg-secondary);
  font: var(--lw-type-body-sm);
  font-style: italic;
  margin: 0 0 var(--lw-space-3);
}

.lw-c-tour-card__controls {
  display: flex;
  flex-wrap: wrap;
  gap: var(--lw-space-2);
  margin-bottom: var(--lw-space-4);
}

.lw-c-tour-card__footnote {
  color: var(--lw-fg-secondary);
  font: var(--lw-type-body-sm);
  margin: 0 0 var(--lw-space-4);
  text-align: center;
}

.lw-c-tour-card__cta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--lw-space-3);
  margin-bottom: var(--lw-space-4);
}

/*
  The closing card sits in the middle of the screen with nothing to point at,
  so it reads as a moment of its own: its words and its two actions are
  centred. Left-aligned buttons under a centred card looked like a mistake.
*/
.lw-c-tour-card--center .lw-c-tour-card__title,
.lw-c-tour-card--center .lw-c-tour-card__body {
  text-align: center;
}

.lw-c-tour-card--center .lw-c-tour-card__cta {
  justify-content: center;
}

/*
  If the row is ever wider than the card it wraps between buttons, never
  inside one ("Leave the" over "walkthrough" read as two links).
*/
.lw-c-tour-card__nav {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: var(--lw-space-2);
}

.lw-c-tour-card__nav > * {
  white-space: nowrap;
}

.lw-c-tour-card__spacer {
  flex: 1;
}
</style>
